import React, { useState, useEffect, useRef } from 'react';
import { useQuery, useApolloClient, gql } from '@apollo/client';
import { pdf } from '@react-pdf/renderer';
import { Content } from '../../components/light/Content';
import { SectionBlock } from '../../components/light/SectionBlock';
import { Loading } from '../../components/Loading';
import { SellerContractPdfDocument } from '../../components/pdf/SellerContractPdfDocument';
import { SellerContractPreviewModal } from '../../components/pdf/SellerContractPreviewModal';
import { SellerContractBatchExportModal } from '../../components/pdf/SellerContractBatchExportModal';

const QUERY_SUPPLIER_CONTRACT_LIST = gql`
  query supplierContractList($year: Int, $supid: Int, $name: String, $limit: Int, $offset: Int) {
    supplierContractList(year: $year, supid: $supid, name: $name, limit: $limit, offset: $offset) {
      items {
        supid
        year
        name
        contact
        legalRep
      }
      total
    }
  }
`;

const QUERY_SUPPLIER_CONTRACT_DETAIL = gql`
  query supplierContractDetail($supid: Int!, $year: Int!) {
    supplierContractDetail(supid: $supid, year: $year) {
      supid
      year
      month
      legalRep
      commission
      handlingFee
      pickupFee
      storageFee
      processingFee
      removalFee
    }
  }
`;

interface SupplierContractItem {
  supid: number;
  year: number;
  name: string;
  contact: {
    email1?: string;
    email2?: string;
    contact1?: string;
    contact2?: string;
    contact3?: string;
  };
  legalRep: string;
}

export function SellerContractScreen(): React.ReactElement {
  const [selectedYear, setSelectedYear] = useState<number>(2021);
  const [searchInput, setSearchInput] = useState<string>('');
  const [appliedSearch, setAppliedSearch] = useState<string>('');
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(20);
  const [exportingId, setExportingId] = useState<number | null>(null);

  // Active Context Menu Dropdown State
  const [activeDropdownId, setActiveDropdownId] = useState<number | null>(null);

  // Preview Modal State
  const [isPreviewOpen, setIsPreviewOpen] = useState<boolean>(false);
  const [previewContract, setPreviewContract] = useState<SupplierContractItem | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [previewDetails, setPreviewDetails] = useState<any[]>([]);
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false);

  // Batch Export Progress Modal State
  const [isBatchModalOpen, setIsBatchModalOpen] = useState<boolean>(false);
  const [batchCurrent, setBatchCurrent] = useState<number>(0);
  const [batchTotal, setBatchTotal] = useState<number>(0);
  const [batchCurrentName, setBatchCurrentName] = useState<string>('');
  const [isBatchFinished, setIsBatchFinished] = useState<boolean>(false);
  const [isBatchExporting, setIsBatchExporting] = useState<boolean>(false);

  const cancelBatchExportRef = useRef<boolean>(false);
  const client = useApolloClient();

  const offset = (page - 1) * pageSize;

  useEffect(() => {
    const handleClickOutside = () => setActiveDropdownId(null);
    window.addEventListener('click', handleClickOutside);
    return () => window.removeEventListener('click', handleClickOutside);
  }, []);

  const { loading, data, refetch } = useQuery<{
    supplierContractList: { items: SupplierContractItem[]; total: number };
  }>(QUERY_SUPPLIER_CONTRACT_LIST, {
    variables: {
      year: selectedYear,
      name: appliedSearch || undefined,
      limit: pageSize,
      offset,
    },
    fetchPolicy: 'network-only',
  });

  const handleFilterSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setAppliedSearch(searchInput);
    setPage(1);
    refetch({
      year: selectedYear,
      name: searchInput || undefined,
      limit: pageSize,
      offset: 0,
    });
  };

  const handlePreviewPdf = async (contract: SupplierContractItem) => {
    try {
      setPreviewContract({ ...contract, year: contract.year || selectedYear });
      setIsPreviewOpen(true);
      setIsPreviewLoading(true);

      const res = await client.query({
        query: QUERY_SUPPLIER_CONTRACT_DETAIL,
        variables: {
          supid: contract.supid,
          year: contract.year || selectedYear,
        },
        fetchPolicy: 'network-only',
      });

      setPreviewDetails(res.data?.supplierContractDetail || []);
    } catch (err) {
      console.error('Failed to load contract preview details:', err);
      alert('Failed to load contract details for preview. Please try again.');
    } finally {
      setIsPreviewLoading(false);
    }
  };

  const handleExportPdf = async (contract: SupplierContractItem) => {
    try {
      setExportingId(contract.supid);

      const res = await client.query({
        query: QUERY_SUPPLIER_CONTRACT_DETAIL,
        variables: {
          supid: contract.supid,
          year: contract.year || selectedYear,
        },
        fetchPolicy: 'network-only',
      });

      const details = res.data?.supplierContractDetail || [];

      const docBlob = await pdf(
        <SellerContractPdfDocument contract={{ ...contract, year: contract.year || selectedYear }} details={details} />,
      ).toBlob();

      const url = URL.createObjectURL(docBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Seller_Contract_${contract.supid}_${contract.year || selectedYear}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export seller contract PDF:', err);
      alert('Failed to export contract PDF. Please try again.');
    } finally {
      setExportingId(null);
    }
  };

  // Batch Export All Seller Contracts for Selected Year One-by-One
  const handleExportAllContracts = async () => {
    try {
      cancelBatchExportRef.current = false;
      setIsBatchExporting(true);
      setIsBatchFinished(false);
      setBatchCurrent(0);
      setBatchTotal(0);
      setBatchCurrentName('Fetching seller contract list...');
      setIsBatchModalOpen(true);

      // Fetch all sellers for selected year
      const res = await client.query({
        query: QUERY_SUPPLIER_CONTRACT_LIST,
        variables: {
          year: selectedYear,
          name: appliedSearch || undefined,
          limit: 1000,
          offset: 0,
        },
        fetchPolicy: 'network-only',
      });

      const allItems: SupplierContractItem[] = res.data?.supplierContractList?.items || [];
      if (allItems.length === 0) {
        setBatchCurrentName(`No seller contracts found for year ${selectedYear}.`);
        setIsBatchFinished(true);
        setIsBatchExporting(false);
        return;
      }

      setBatchTotal(allItems.length);

      // Export one by one sequentially
      for (let i = 0; i < allItems.length; i++) {
        if (cancelBatchExportRef.current) {
          setBatchCurrentName('Batch export cancelled by user.');
          break;
        }

        const contract = allItems[i];
        const storeLabel = contract.name ? `${contract.name} (ID: ${contract.supid})` : `Seller ${contract.supid}`;
        setBatchCurrent(i + 1);
        setBatchCurrentName(`Exporting ${i + 1} of ${allItems.length}: ${storeLabel}...`);

        const detailRes = await client.query({
          query: QUERY_SUPPLIER_CONTRACT_DETAIL,
          variables: {
            supid: contract.supid,
            year: contract.year || selectedYear,
          },
          fetchPolicy: 'network-only',
        });

        const details = detailRes.data?.supplierContractDetail || [];

        const docBlob = await pdf(
          <SellerContractPdfDocument
            contract={{ ...contract, year: contract.year || selectedYear }}
            details={details}
          />,
        ).toBlob();

        const url = URL.createObjectURL(docBlob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Seller_Contract_${contract.supid}_${contract.year || selectedYear}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // Pause 350ms between downloads so browser downloads handle cleanly
        await new Promise(r => setTimeout(r, 350));
      }

      if (!cancelBatchExportRef.current) {
        setIsBatchFinished(true);
        setBatchCurrentName(`Successfully exported all ${allItems.length} contract PDFs!`);
      }
    } catch (err) {
      console.error('Error during batch contract PDF export:', err);
      setBatchCurrentName('Error occurred during export. Please try again.');
      setIsBatchFinished(true);
    } finally {
      setIsBatchExporting(false);
    }
  };

  const handleCancelBatchExport = () => {
    cancelBatchExportRef.current = true;
    setIsBatchFinished(true);
    setBatchCurrentName('Batch export stopped by user.');
  };

  const years = [2021, 2022, 2023, 2024, 2025, 2026, 2027];
  const items = data?.supplierContractList?.items || [];
  const total = data?.supplierContractList?.total || 0;
  const totalPages = Math.ceil(total / pageSize) || 1;

  const startEntry = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endEntry = Math.min(page * pageSize, total);

  return (
    <Content>
      <SectionBlock title="Seller Contracts">
        <div
          className="controls-above-table"
          style={{
            marginBottom: 15,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
          }}
        >
          <form
            onSubmit={handleFilterSubmit}
            style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}
          >
            <div>
              <label style={{ marginRight: 5, fontWeight: 600 }}>Year:</label>
              <select
                className="form-control"
                style={{ width: 110, display: 'inline-block' }}
                value={selectedYear}
                onChange={e => {
                  const y = Number(e.target.value);
                  setSelectedYear(y);
                }}
              >
                {years.map(y => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ marginRight: 5, fontWeight: 600 }}>Search:</label>
              <input
                type="text"
                className="form-control"
                placeholder="Seller Name or Representative..."
                style={{ width: 260, display: 'inline-block' }}
                value={searchInput}
                onChange={e => setSearchInput(e.target.value)}
              />
            </div>

            <button type="submit" className="btn btn-primary">
              <i className="fas fa-search" style={{ marginRight: 5 }}></i> Filter
            </button>
          </form>

          {/* Top Right Export All Contracts Button */}
          <div>
            <button
              type="button"
              className="btn btn-danger"
              disabled={isBatchExporting}
              onClick={handleExportAllContracts}
              title={`Export all seller contract PDFs for ${selectedYear} one-by-one`}
            >
              {isBatchExporting ? (
                <span>
                  <i className="fas fa-spinner fa-spin" style={{ marginRight: 6 }}></i> Exporting All...
                </span>
              ) : (
                <span>
                  <i className="fas fa-file-export" style={{ marginRight: 6 }}></i> Export All Contracts ({selectedYear}
                  )
                </span>
              )}
            </button>
          </div>
        </div>

        {loading ? (
          <Loading />
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-striped table-bordered">
                <thead>
                  <tr>
                    <th>Seller ID</th>
                    <th>Year</th>
                    <th>Store Name</th>
                    <th>Legal Representative</th>
                    <th>Contact Info</th>
                    <th style={{ width: 80, textAlign: 'center' }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? (
                    items.map((item, idx) => {
                      const phone = item.contact?.contact1 || item.contact?.contact2 || '-';
                      const email = item.contact?.email1 || item.contact?.email2 || '-';
                      const isExporting = exportingId === item.supid;
                      const isDropdownOpen = activeDropdownId === item.supid;

                      return (
                        <tr key={`${item.supid}-${item.year}-${idx}`}>
                          <td>{item.supid}</td>
                          <td>{item.year}</td>
                          <td>
                            <strong>{item.name || '-'}</strong>
                          </td>
                          <td>{item.legalRep || '-'}</td>
                          <td>
                            <div>
                              <i className="fas fa-phone-alt" style={{ marginRight: 5, fontSize: 11 }}></i>
                              {phone}
                            </div>
                            <div>
                              <i className="fas fa-envelope" style={{ marginRight: 5, fontSize: 11 }}></i>
                              {email}
                            </div>
                          </td>
                          <td style={{ textAlign: 'center', position: 'relative' }}>
                            <button
                              type="button"
                              className="btn btn-sm btn-light"
                              style={{
                                borderRadius: '50%',
                                width: 32,
                                height: 32,
                                padding: 0,
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid #dee2e6',
                              }}
                              onClick={e => {
                                e.stopPropagation();
                                setActiveDropdownId(isDropdownOpen ? null : item.supid);
                              }}
                              title="Actions Context Menu"
                            >
                              <i className="fas fa-ellipsis-v" style={{ fontSize: 14, color: '#495057' }}></i>
                            </button>

                            {isDropdownOpen && (
                              <div
                                className="dropdown-menu show"
                                style={{
                                  position: 'absolute',
                                  right: 10,
                                  top: 42,
                                  zIndex: 1000,
                                  minWidth: 175,
                                  boxShadow: '0 6px 18px rgba(0,0,0,0.15)',
                                  borderRadius: 6,
                                  padding: '6px 0',
                                  backgroundColor: '#fff',
                                }}
                                onClick={e => e.stopPropagation()}
                              >
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => {
                                    setActiveDropdownId(null);
                                    handlePreviewPdf(item);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 16px',
                                    fontSize: 13,
                                  }}
                                >
                                  <i className="fas fa-eye text-info" style={{ width: 16 }}></i> Quick Preview
                                </button>

                                <a
                                  className="dropdown-item"
                                  href={`/audit/seller-contract-preview?supid=${item.supid}&year=${item.year ||
                                    selectedYear}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  onClick={() => setActiveDropdownId(null)}
                                  style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 16px',
                                    fontSize: 13,
                                  }}
                                >
                                  <i className="fas fa-external-link-alt text-primary" style={{ width: 16 }}></i> Live
                                  Preview Page
                                </a>

                                <div className="dropdown-divider" style={{ margin: '4px 0' }}></div>

                                <button
                                  type="button"
                                  className="dropdown-item text-danger"
                                  disabled={isExporting}
                                  onClick={() => {
                                    setActiveDropdownId(null);
                                    handleExportPdf(item);
                                  }}
                                  style={{
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    padding: '8px 16px',
                                    fontSize: 13,
                                  }}
                                >
                                  {isExporting ? (
                                    <>
                                      <i className="fas fa-spinner fa-spin" style={{ width: 16 }}></i> Exporting...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fas fa-file-pdf" style={{ width: 16 }}></i> Export PDF
                                    </>
                                  )}
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan={6} style={{ textAlign: 'center', padding: 20 }}>
                        No seller contracts found for year {selectedYear}.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* List Footer Pagination */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginTop: 15,
                paddingTop: 10,
                borderTop: '1px solid #eee',
                flexWrap: 'wrap',
                gap: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 13, color: '#555' }}>Per page:</span>
                <select
                  className="form-control form-control-sm"
                  style={{ width: 75 }}
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span style={{ fontSize: 13, color: '#666' }}>
                  Showing {startEntry} to {endEntry} of {total} entries
                </span>
              </div>

              <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                  title="First Page"
                >
                  &laquo;
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page <= 1}
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span style={{ fontSize: 13, padding: '0 8px', fontWeight: 600 }}>
                  Page {page} of {totalPages}
                </span>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
                <button
                  className="btn btn-sm btn-outline-secondary"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  title="Last Page"
                >
                  &raquo;
                </button>
              </div>
            </div>
          </>
        )}
      </SectionBlock>

      {/* Contract PDF Interactive Preview Modal */}
      <SellerContractPreviewModal
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        contract={previewContract}
        details={previewDetails}
        loading={isPreviewLoading}
      />

      {/* Sequential Batch Export Progress Modal */}
      <SellerContractBatchExportModal
        isOpen={isBatchModalOpen}
        year={selectedYear}
        current={batchCurrent}
        total={batchTotal}
        currentName={batchCurrentName}
        isFinished={isBatchFinished}
        onCancel={handleCancelBatchExport}
        onClose={() => setIsBatchModalOpen(false)}
      />
    </Content>
  );
}
